/** Session IAM temporaire pour le parcours /trial (compte existant). */

const HANDOFF_MAX_AGE_MS = 5 * 60_000;
const STORAGE_KEY = "pep-trial-iam-session";
const SESSION_MAX_AGE_MS = 60 * 60_000;

export interface TrialIamSession {
  accessToken: string;
  tenantSlug: string;
  tenantName: string;
  email: string;
  modules: string[];
  issuedAt: number;
}

interface HandoffPayload {
  session?: {
    tokens?: { accessToken?: string };
    tenant?: { slug?: string; name?: string };
    user?: { email?: string };
    licensedModules?: string[];
  };
  issuedAt: number;
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const part = token.split(".")[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function parseHandoffHash(hash: string): TrialIamSession | null {
  const raw = hash.replace(/^#/, "");
  const match = raw.match(/(?:^|&)payload=([^&]+)/);
  if (!match) return null;

  try {
    const b64 = decodeURIComponent(match[1].replace(/\+/g, "%2B"));
    const json = decodeURIComponent(atob(b64));
    const payload = JSON.parse(json) as HandoffPayload;
    const accessToken = payload.session?.tokens?.accessToken?.trim();
    if (!accessToken) return null;
    if (Date.now() - payload.issuedAt > HANDOFF_MAX_AGE_MS) return null;

    const claims = decodeJwtPayload(accessToken);
    const tenantSlug =
      payload.session?.tenant?.slug?.trim() ||
      String(claims?.tenantSlug ?? "").trim();
    const email =
      payload.session?.user?.email?.trim() ||
      String(claims?.email ?? "").trim();

    if (!tenantSlug || !email) return null;

    const modules =
      payload.session?.licensedModules ??
      (Array.isArray(claims?.modules) ? (claims.modules as string[]) : []);

    return {
      accessToken,
      tenantSlug,
      tenantName: payload.session?.tenant?.name?.trim() || tenantSlug,
      email,
      modules,
      issuedAt: payload.issuedAt,
    };
  } catch {
    return null;
  }
}

export function saveTrialSession(session: TrialIamSession): void {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

export function loadTrialSession(): TrialIamSession | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw) as TrialIamSession;
    if (!session.accessToken || Date.now() - session.issuedAt > SESSION_MAX_AGE_MS) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

export function clearTrialSession(): void {
  sessionStorage.removeItem(STORAGE_KEY);
}

/** Lit le fragment handoff Suite, persiste la session et nettoie l'URL. */
export function consumeTrialHandoffFromLocation(): TrialIamSession | null {
  const fromHash = parseHandoffHash(window.location.hash);
  if (fromHash) {
    saveTrialSession(fromHash);
    const url = new URL(window.location.href);
    url.hash = "";
    window.history.replaceState(null, "", url.pathname + url.search);
    return fromHash;
  }
  return loadTrialSession();
}

export function parseTrialHandoffHash(hash: string): TrialIamSession | null {
  return parseHandoffHash(hash);
}
